/* Copyright (c) 2020-present CloudIO Inc */

import { IoPageAccess, IoPages } from '@cloudio-saas/datasource-types';
import {
  useStateXAtom,
  useStateXAtomValue,
  useStateXResetter,
  useStateXSetter,
} from '@cloudio-saas/statex';
import {
  Typography,
  Divider,
  IconButton,
  InputBase,
  Theme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import Controller from 'controller/Controller';
import useDebounce from 'hooks/useDebounce';
import Icon from 'icons/Icon';
import { usePending } from 'platform/PlatformHooks';
import { PlatformContext } from 'platform/PlatformProvider';
import React, { useContext, useEffect, useRef, useState } from 'react';
import Store from 'store/Store';
import { DBRow, isFromDB } from 'store/storeTypes';
import { useStoreCurrentRecord, useStoreDirty } from 'store/useAppStore';
import useDataStore from 'store/useDataStore';
import {
  getIoPagesListStoreProps,
  useIoPagesEditStore,
} from 'store/utils/utils';
import { IconPulse } from 'utils/Pulse';
import WaveDots from 'utils/WaveDots';
import {
  PageView,
  sidebarActivePageIdAtom,
  sidebarPagesFilterAtom,
  sidebarPagesFilterResultsSizeAtom,
  sidebarPageWizardDataSourceAtom,
  sidebarPageWizardStepAtom,
  sidebarPageWizardTitleAtom,
} from '../../atoms';
import ExpandCollapseIcon from '../ExpandCollapseIcon';
import { isViewOnlyDeveloper } from 'root/useUser';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '2px 4px',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  iconButton: {
    padding: 10,
  },
  divider: {
    height: 28,
    margin: 4,
  },
}));

export default function PagesHeader({
  setView,
  view,
  store: listStore,
  appUid,
}: {
  appUid: string;
  setView: (_view: PageView) => void;
  view: PageView;
  store: Store<IoPages>;
}): React.ReactElement {
  const platform = useContext(PlatformContext);
  const store = useIoPagesEditStore(appUid);
  const dirty = useStoreDirty(store);
  const reset = useStateXResetter();
  const set = useStateXSetter();
  const filterSize = useStateXAtomValue(sidebarPagesFilterResultsSizeAtom);
  const { record, isBusy } = useStoreCurrentRecord(store);
  const classes = useStyles();
  const [search, setSearch] = useStateXAtom(sidebarPagesFilterAtom);
  const [filter, setFilter] = useState(search);
  const [isPending, setPending] = usePending();
  const [isPending2, setPending2] = usePending();
  const accessStore = useDataStore<IoPageAccess>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPageAccessV',
    alias: 'IoPageAccessV-alias',
  });
  const [setDebounceValue] = useDebounce((val: string) => {
    setSearch(val);
  });
  useEffect(() => {
    setDebounceValue(300, filter);
  }, [filter, setDebounceValue]);

  const ref = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useStateXAtom(sidebarPageWizardTitleAtom);

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFilter(e.target.value);
  }

  const handleRefresh = () => {
    platform.startAsyncTransition(async () => {
      await listStore.refresh();
    }, setPending2);
  };

  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);
  const pagesStore = useDataStore<IoPages>(getIoPagesListStoreProps(appUid));

  return (
    <div className={classes.root}>
      <IconButton
        data-testid="back-button"
        onClick={() => {
          if (view === 'edit') {
            listStore.reset();
            store.reset();
            setView('list');
          } else if (view === 'wizard' || view === 'next') {
            setView('list');
          } else {
            ref.current?.focus();
          }
        }}
        className={classes.iconButton}
        aria-label="Back"
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          {view === 'list' ? 'Filter' : 'Back'}
        </span>
        <Icon iconName={view === 'list' ? 'search' : 'chevron-left'} />
        {view !== 'list' ? (
          <IconPulse
            pulseKey="page_back"
            pulseMessage="Click this back icon to discard your changes (if any) and navigate back to the list view"
          />
        ) : null}
      </IconButton>
      {view === 'wizard' || view === 'edit' || view === 'next' ? (
        <Typography className={classes.input}>
          {title || (view === 'edit' ? 'Edit Page' : view)}
        </Typography>
      ) : (
        <InputBase
          data-testid="filter"
          inputRef={ref}
          className={classes.input}
          placeholder="Search Pages"
          inputProps={{
            'aria-label': 'search pages',
            'data-testid': 'search-pages',
          }}
          value={filter ?? ''}
          onChange={onChange}
          autoFocus
        />
      )}
      {view === 'list' ? (
        <Typography color="textSecondary" variant="body2">
          {filterSize} pages
        </Typography>
      ) : null}
      <Divider className={classes.divider} orientation="vertical" />
      {view !== 'wizard' && view !== 'next' && (view !== 'edit' || dirty) ? (
        <IconButton
          disabled={isViewOnlyUser}
          data-tip={view === 'edit' ? 'Save' : 'Create New Page'}
          data-testid={view === 'edit' ? 'page-save' : 'page-create'}
          color="primary"
          aria-label={view === 'edit' ? 'Save' : 'Create New Page'}
          onClick={async () => {
            if (isPending || isBusy) return;
            if (view === 'edit') {
              const fromDB = isFromDB(record);
              if (!record?.pageName) {
                platform.showError('You must provide a name for this page!');
                return;
              }
              if (!record?.pageUid) {
                platform.showError('You must provide a code for this page!');
                return;
              }
              platform.startAsyncTransition(async () => {
                const row = store.getCurrentRecord() as DBRow<IoPages>;
                const old_lud = row.lastUpdateDate;
                const r = await store.save({ feedback: 'NONE' });
                if (r) {
                  let feedback = '';
                  const dbrow = store.getCurrentRecord() as DBRow<IoPages>;
                  if (fromDB) {
                    feedback = `Page '${dbrow.pageName}' updated successfully!`;
                    const listRow = listStore.getCurrentRecord();
                    const _id = listStore.getCurrentRecordId();
                    if (
                      old_lud ===
                      platform.getter([
                        'db',
                        'IoPages',
                        dbrow.pageUid,
                        'lastUpdateDate',
                      ])
                    ) {
                      // the page is being edited in designer and the lud matches
                      // hence update lud to the edit can be saved without refresh
                      platform.setter(
                        ['db', 'IoPages', dbrow.pageUid, 'lastUpdateDate'],
                        dbrow.lastUpdateDate,
                      );
                      platform.setter(
                        ['db', 'IoPages', dbrow.pageUid, 'lastUpdatedBy'],
                        dbrow.lastUpdatedBy,
                      );
                    }

                    if (_id && listRow?.pageUid === dbrow.pageUid) {
                      listStore.replaceClientId(_id, _id, {
                        ...dbrow,
                      });
                    } else {
                      await listStore.refresh();
                    }
                  } else {
                    feedback = `Page '${dbrow.pageName}' created successfully!`;
                    await accessStore.query({
                      props: {
                        params: { appUid, pageUid: dbrow.pageUid },
                      },
                    });
                    await pagesStore.refresh();
                    const listRow = listStore.getCurrentRecord();
                    if (listRow && dbrow && listRow._cid) {
                      listStore.replaceClientId(listRow._cid, dbrow._id, {
                        ...dbrow,
                      });
                    }
                    listStore.setCurrentRecordId(dbrow._id);
                    setTitle('Choose the next step');
                    set(sidebarActivePageIdAtom, dbrow.pageUid);
                    // platform.goToPage(appUid, dbrow.pageUid);
                    setView('next');
                  }
                  platform.showSuccess(feedback);
                  if (dbrow.pageUid) {
                    await Controller.compile({
                      appUid,
                      pageId: dbrow.pageUid,
                      platform,
                      source: dbrow.controller,
                      fileName: `${dbrow.pageUid}_PageController.tsx`,
                      debug: true,
                    });
                  }
                }
              }, setPending);
            } else {
              setTitle('How do you want to create?');
              setView('wizard');
              reset(sidebarPageWizardStepAtom);
              reset(sidebarPageWizardDataSourceAtom);
            }
          }}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            {view === 'edit' ? 'Save' : 'Create New Page'}
          </span>
          {isPending || isBusy ? (
            <WaveDots active />
          ) : (
            <Icon
              disabled={isViewOnlyUser}
              color="primary"
              iconName={view === 'edit' ? 'check' : 'plus'}
            />
          )}
          {view !== 'edit' ? (
            <IconPulse
              pulseKey="new_page"
              pulseMessage="Click this plus icon to create a new page"
            />
          ) : null}
          {view === 'edit' ? (
            <IconPulse
              pulseKey="save_page"
              pulseMessage="Click this check icon to save your work, when you are done with your changes"
            />
          ) : null}
        </IconButton>
      ) : null}
      {view === 'list' ? (
        <IconButton
          data-testid="refresh-pages"
          disabled={isPending2}
          data-tip="Refresh"
          color="primary"
          aria-label="refresh"
          onClick={handleRefresh}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Refresh
          </span>
          {isPending2 ? (
            <WaveDots active />
          ) : (
            <Icon color="primary" iconName="redo" />
          )}
        </IconButton>
      ) : null}
      <ExpandCollapseIcon />
    </div>
  );
}
